import React from 'react'
import { Box, Container, Grid, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Logo from 'static/images/logo.svg'

const Footer = () => {
  const theme = useTheme()

  const footerSections = [
    {
      title: 'Jobs',
      titleHref: '/jobs',
      links: [
        { text: 'Directory', href: '/directory' },
        { text: 'Housing', href: '/housing' },
        { text: 'Salary', href: '/salary' },
        { text: 'Residency Transfer', href: '/residency-transfer' },
      ]
    },
    {
      title: 'About Us',
      titleHref: '/about',
      links: [
        { text: 'Contact Us', href: '/contact' },
        { text: 'Support', href: '/support' },
        { text: 'Partner With Us', href: '/register/hospitals' },
      ]
    }
  ]

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'white',
        py: 6,
        borderTop: '1px solid #e0e0e0',
        '& .footer-link': {
          textDecoration: 'none !important',
          borderBottom: 'none !important',
          borderTop: 'none !important',
          borderLeft: 'none !important',
          borderRight: 'none !important',
          outline: 'none !important',
          boxShadow: 'none !important',
          background: 'transparent !important',
          backgroundImage: 'none !important',
          transition: 'color 0.2s ease !important',
          transform: 'none !important',
          animation: 'none !important',
          '&::before': {
            display: 'none !important',
            visibility: 'hidden !important'
          },
          '&::after': {
            display: 'none !important',
            visibility: 'hidden !important'
          },
          '&:hover': {
            textDecoration: 'none !important',
            borderBottom: 'none !important',
            borderTop: 'none !important',
            borderLeft: 'none !important',
            borderRight: 'none !important',
            outline: 'none !important',
            boxShadow: 'none !important',
            background: 'transparent !important',
            backgroundImage: 'none !important',
            transform: 'none !important',
            animation: 'none !important',
            '&::before': {
              display: 'none !important',
              visibility: 'hidden !important'
            },
            '&::after': {
              display: 'none !important',
              visibility: 'hidden !important'
            }
          },
          '&:focus': {
            textDecoration: 'none !important',
            borderBottom: 'none !important',
            outline: 'none !important'
          },
          '&:active': {
            textDecoration: 'none !important',
            borderBottom: 'none !important'
          },
          '&:visited': {
            textDecoration: 'none !important',
            borderBottom: 'none !important'
          }
        }
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Left Side - Logo and Description */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              {/* Logo */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <img 
                  src={Logo} 
                  alt="MyStethi Logo"
                  style={{
                    height: '32px',
                    width: 'auto',
                    marginRight: '16px'
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.grey[900],
                    fontSize: '1.5rem'
                  }}
                >
                  MyStethi
                </Typography>
              </Box>

              {/* Description */}
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.grey[900],
                  lineHeight: 1.6,
                  fontSize: '0.9rem',
                  maxWidth: '300px'
                }}
              >
                Connecting healthcare professionals directly with hospitals and health systems. 
                No spam, no games, just quality job opportunities and direct connections.
              </Typography>
            </Box>
          </Grid>

          {/* Right Side - Navigation Links */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={4} sx={{ justifyContent: 'flex-end' }}>
              {footerSections.map((section, index) => (
                <Grid item xs={6} sm={4} key={index}>
                  <Box
                    component="a"
                    href={section.titleHref}
                    className="footer-link"
                    sx={{
                      fontWeight: 400,
                      color: theme.palette.grey[900],
                      mb: 1,
                      fontSize: '0.9rem',
                      display: 'block',
                      cursor: 'pointer',
                      '&:hover': {
                        color: theme.palette.primary.main
                      }
                    }}
                  >
                    {section.title}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {section.links.map((link, linkIndex) => (
                      <Box
                        key={linkIndex}
                        component="a"
                        href={link.href}
                        className="footer-link"
                        sx={{
                          color: theme.palette.grey[900],
                          fontSize: '0.9rem',
                          fontWeight: 400,
                          cursor: 'pointer',
                          backgroundColor: 'transparent !important',
                          border: 'none !important',
                          padding: '0 !important',
                          margin: '0 !important',
                          boxShadow: 'none !important',
                          '&:hover': {
                            color: theme.palette.primary.main,
                            backgroundColor: 'transparent !important'
                          }
                        }}
                      >
                        {link.text}
                      </Box>
                    ))}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {/* Bottom Section - Legal Links and Copyright */}
        <Box
          sx={{
            borderTop: `1px solid ${theme.palette.primary.main}`,
            mt: 4,
            pt: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}
        >
          {/* Legal Links */}
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box
              component="a"
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
              sx={{
                color: theme.palette.grey[900],
                fontSize: '0.9rem',
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main
                }
              }}
            >
              Privacy Policy
            </Box>
            <Box
              component="a"
              href="/tac"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
              sx={{
                color: theme.palette.grey[900],
                fontSize: '0.9rem',
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main
                }
              }}
            >
              Terms & Conditions
            </Box>
          </Box>

          {/* Copyright */}
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.grey[900],
              fontSize: '0.9rem'
            }}
          >
            All Rights Reserved. © MyStethi 2025
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

export default Footer
